/**
 * Papers-only citation parsing for paperclip chat.
 *
 * In claude_engine engine_mode, the backend returns:
 *   - Inline `[N]` markers in the response body
 *   - A REFERENCES block at the end with citation URLs
 *
 * This module parses the REFERENCES block to extract citation metadata
 * and maps `[N]` to clickable links. Old citations.gxl.ai URLs and new
 * paperclip.gxl.ai/citations URLs are both accepted; display rewrites the
 * old host to paperclip, and sidebar metadata uses same-origin `/citations/...`.
 */

import type { TraceTurn } from "../types";

export interface Citation {
  id: number;
  doc_id: string;
  block_id: string;
  content: string;
  source_type?: "biorxiv_xml" | "medrxiv_xml" | "pmc_xml" | "paper_link" | "supplement_pdf" | "pdf" | "patent";
  line_number?: number;
  source?: string;
  doi?: string;
  section?: string;
  xpath?: string;
  source_path?: string;
  xml_id?: string;
  public_url?: string;
  doc_title?: string;
  authors?: string;
  month_year?: string;
  journal?: string;
  highlight_available?: boolean;
  _citationUrl?: string;
}

export const PAPER_XML_TYPES = new Set([
  "biorxiv_xml",
  "medrxiv_xml",
  "pmc_xml",
]);
export const PAPER_SOURCES = new Set(["biorxiv", "medrxiv", "pmc"]);

export const isPaperXmlType = (sourceType: string | undefined): boolean => {
  return !!sourceType && PAPER_XML_TYPES.has(sourceType);
}

export const isPaperSource = (source: string | undefined): boolean => {
  return !!source && PAPER_SOURCES.has(source);
}

export const isPmcId = (id: string | undefined): boolean => {
  return !!id && /^PMC\d+$/i.test(id);
}

export const detectPaperSource = (
  docId: string | undefined,
  source?: string,
): "biorxiv" | "medrxiv" | "pmc" => {
  if (docId?.startsWith("PMC")) return "pmc";
  if (docId?.startsWith("bio_")) return "biorxiv";
  if (docId?.startsWith("med_")) return "medrxiv";
  if (source && PAPER_SOURCES.has(source))
    return source as "biorxiv" | "medrxiv" | "pmc";
  return "biorxiv";
}

// ── URL citation parsing (claude_engine REFERENCES block) ──

const CITATION_DOC_TYPES = "papers|fda|trials|patents|clinicaltrials|sec";

const rewriteCitationUrl = (url: string): string => {
  return url.replace(
    /https:\/\/(?:citations\.gxl\.ai|paperclip(?:\.staging)?\.gxl\.ai\/citations)/g,
    "/citations",
  );
}

const URL_CITATION_RE = new RegExp(
  `\\[(\\d+)\\]\\s+([^\\[]{0,500}?)https:\\/\\/(?:citations\\.gxl\\.ai|paperclip(?:\\.staging)?\\.gxl\\.ai\\/citations)\\/(${CITATION_DOC_TYPES})\\/([^\\s#]+)(?:#([^\\s)}\\]]+))?`,
  "g",
);

interface UrlCitationMatch {
  fullMatch: string;
  start: number;
  end: number;
  bracketNum: number;
  text: string;
  urlType: string;
  docId: string;
  lineNumbers: number[];
  citationUrl: string;
}

const findUrlCitations = (text: string): UrlCitationMatch[] => {
  const matches: UrlCitationMatch[] = [];
  const re = new RegExp(URL_CITATION_RE.source, URL_CITATION_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const lineNums: number[] = [];
    if (m[5]) {
      for (const part of m[5].split(",")) {
        const num = parseInt(part.replace(/^L/, ""), 10);
        if (!isNaN(num)) lineNums.push(num);
      }
    }
    const urlPath = `${m[3]}/${m[4]}${m[5] ? "#" + m[5] : ""}`;
    matches.push({
      fullMatch: m[0],
      start: m.index,
      end: m.index + m[0].length,
      bracketNum: parseInt(m[1], 10),
      text: m[2].trim(),
      urlType: m[3],
      docId: m[4],
      lineNumbers: lineNums,
      citationUrl: rewriteCitationUrl(`https://citations.gxl.ai/${urlPath}`),
    });
  }
  return matches;
}

/**
 * Build Citation objects from URL citation lines in the REFERENCES block.
 * Preserves bracket numbers as IDs so [N] in text maps directly to citation.id.
 */
export const extractCitations = (text: string): Citation[] => {
  const matches = findUrlCitations(text);
  const citations: Citation[] = [];
  const seenKeys = new Set<string>();

  for (const m of matches) {
    const key = `url_${m.docId}_${m.lineNumbers.join(",")}`;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    let sourceType: Citation["source_type"];
    let source: string;
    if (m.urlType === "fda") {
      sourceType = "pdf";
      source = "fda";
    } else if (m.urlType === "trials" || m.urlType === "clinicaltrials") {
      sourceType = "pmc_xml";
      source = "trials";
    } else if (m.urlType === "patents") {
      sourceType = "patent";
      source = "patents";
    } else if (m.urlType === "sec") {
      sourceType = "pdf";
      source = "sec";
    } else {
      source = detectPaperSource(m.docId);
      sourceType = `${source}_xml` as Citation["source_type"];
    }

    citations.push({
      id: m.bracketNum,
      doc_id: m.docId,
      block_id:
        m.lineNumbers.length > 0
          ? `line_${m.lineNumbers[0]}`
          : `doc_${m.docId}`,
      content: m.text.replace(/\s+/g, " ").replace(/[\u201C\u201D]/g, '"'),
      source_type: sourceType,
      line_number: m.lineNumbers[0],
      source,
      public_url: m.citationUrl,
      _citationUrl: m.citationUrl,
    });
  }

  return citations;
}

/** Text used for citation extraction from a single assistant TraceTurn. */
export const getTurnCitationText = (turn: TraceTurn): string => {
  const textParts: string[] = [];
  for (const item of turn.items ?? []) {
    if (item.type === "text") textParts.push(item.content);
  }
  if (turn.finalResponse) textParts.push(turn.finalResponse);
  if (turn.content) textParts.push(turn.content);
  return textParts.join("\n").trim();
};

/**
 * Extract citations for one assistant response group (consecutive assistant
 * TraceTurns for a single user prompt). Joins all turn text so in-text `[N]`
 * in an earlier turn still resolve against REFERENCES on a later turn.
 */
export const extractGroupCitations = (turns: TraceTurn[]): Citation[] => {
  const joined = turns
    .filter((t) => t.type === "assistant")
    .map(getTurnCitationText)
    .filter(Boolean)
    .join("\n");
  if (!joined.trim()) return [];
  return extractCitations(joined);
};

/** Stable identity for sidebar selection / prev-next within a sibling list. */
export const citationStableKey = (c: Citation): string =>
  `${c.id}|${c.doc_id}|${c.line_number ?? ""}|${c._citationUrl ?? c.public_url ?? ""}`;

// ── Display helpers ──

export const cleanTextForDisplay = (text: string, isStreaming = false, sessionId?: string): string => {
  let result = text;

  result = result.replace(/[\u201C\u201D]/g, '"');
  result = result.replace(/[\u2018\u2019]/g, "'");
  result = result.replace(/<function_calls>[\s\S]*?<\/function_calls>/g, "");
  result = result.replace(/<function_result>[\s\S]*?<\/function_result>/g, "");
  result = result.replace(/<tool_calls>[\s\S]*?<\/tool_calls>/g, "");
  result = result.replace(/<tool_result>[\s\S]*?<\/tool_result>/g, "");
  result = result.replace(/---FINAL_RESPONSE---\s*/g, "");
  result = result.replace(/---END_FINAL_RESPONSE---\s*/g, "");

  // Old stored chats still contain citations.gxl.ai; rewrite to the paperclip host.
  // Keep absolute https so GFM continues to autolink bare REFERENCES URLs.
  result = result.replace(
    /https:\/\/citations\.gxl\.ai/g,
    "https://paperclip.gxl.ai/citations",
  );

  // Normalize the REFERENCES heading regardless of blank line / CRLF / bold /
  // trailing spaces. A single newline alone is a soft break in GFM, so always
  // emit a blank line so the heading stays on its own paragraph above [1].
  result = result.replace(
    /\*{0,2}\bREFERENCES\*{0,2}[ \t]*\r?\n(?:\r?\n)?/g,
    "**References**\n\n",
  );

  if (isStreaming) {
    result = result.replace(/<function_calls>[\s\S]*$/g, "");
    result = result.replace(/<function_result>[\s\S]*$/g, "");
    result = result.replace(/<tool_calls>[\s\S]*$/g, "");
    result = result.replace(/<tool_result>[\s\S]*$/g, "");
  }

  if (sessionId) {
    result = result.replace(
      /\{\{?\s*"type"\s*:\s*"image"\s*,\s*"path"\s*:\s*"([^"]+)"\s*\}?\}/g,
      (_match, path) => `![${path}](/api/chat/sessions/${sessionId}/binaries/${path})`,
    );
  }

  return result;
}

export const resolvePaperDisplay = (
  source: string | undefined,
  docId: string | undefined,
  doi: string | undefined,
  publicUrl: string | undefined,
) => {
  const isPmc = isPmcId(docId) || source === "pmc" || source?.startsWith("PMC");
  const resolvedSource = isPmc ? "pmc" : source || "biorxiv";
  const label = isPmc
    ? "PubMed Central"
    : resolvedSource === "medrxiv"
      ? "medRxiv"
      : "bioRxiv";
  const isPreprint = !isPmc;
  const url =
    publicUrl ||
    (doi
      ? isPmc
        ? `https://doi.org/${doi}`
        : `https://www.${resolvedSource}.org/content/${doi}`
      : undefined);
  return { source: resolvedSource, label, isPreprint, url };
}
